import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select,
    Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { OperatorOrder } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';

const STATUSES = ['pending', 'paid', 'completed', 'partially_refunded', 'refunded', 'payment_failed', 'cancelled'];
const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');

/** Betrag "12,50" oder "12.50" → "12.50" (auf zwei Stellen), sonst null. */
export const parseAmount = (input: string): string | null => {
    const normalized = input.trim().replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
    return Number(normalized).toFixed(2);
};

const RefundDialog: React.FC<{ order: OperatorOrder | null; onClose: () => void; onDone: () => void }> = ({ order, onClose, onDone }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [mode, setMode] = useState<'full' | 'partial'>('full');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setMode('full');
        setAmount('');
        setReason('');
        setError(null);
    }, [order]);

    if (!order) return null;
    const max = order.refundableAmount;

    const submit = async () => {
        let value: string | undefined;
        if (mode === 'partial') {
            const parsed = parseAmount(amount);
            if (parsed === null || Number(parsed) <= 0 || Number(parsed) > Number(max)) {
                setError(translate('marketplaceOperator.orders.refundAmountInvalid', { max: formatEuro(max, order.currency) }));
                return;
            }
            value = parsed;
        }
        setBusy(true);
        setError(null);
        try {
            await marketplaceOperatorService.refund(order.uuid, { amount: value, reason: reason.trim() || undefined });
            notify(translate('marketplaceOperator.orders.refunded'), { type: 'success' });
            onDone();
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="refund-title">
            <DialogTitle id="refund-title">{translate('marketplaceOperator.orders.refundTitle', { number: order.orderNumber })}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{translate('marketplaceOperator.orders.refundIntro')}</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'full' | 'partial')}>
                    <FormControlLabel value="full" control={<Radio />} label={translate('marketplaceOperator.orders.refundFull', { amount: formatEuro(max, order.currency) })} />
                    <FormControlLabel value="partial" control={<Radio />} label={translate('marketplaceOperator.orders.refundPartial')} />
                </RadioGroup>
                {mode === 'partial' && (
                    <TextField
                        label={translate('marketplaceOperator.orders.refundAmount')}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    />
                )}
                <TextField
                    label={translate('marketplaceOperator.orders.refundReason')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 255 } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy}>{translate('marketplaceOperator.common.cancel')}</Button>
                <Button variant="contained" color="error" onClick={submit} disabled={busy}>{translate('marketplaceOperator.orders.refundConfirm')}</Button>
            </DialogActions>
        </Dialog>
    );
};

/** Plattformweite Bestellungen mit Filtern und Refund (voll/teilweise) über `MarketplaceRefundService`. */
const OperatorOrdersPanel: React.FC = () => {
    const translate = useTranslate();
    const [orders, setOrders] = useState<OperatorOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [status, setStatus] = useState('');
    const [channel, setChannel] = useState('');
    const [refunding, setRefunding] = useState<OperatorOrder | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setOrders(await marketplaceOperatorService.getOrders({ q: appliedQuery, status, channel }));
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [appliedQuery, status, channel, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedQuery, status, channel]);

    const party = (name: string | null, id: number | null) => name ?? (id ? translate('marketplaceOperator.common.companyFallback', { id }) : '–');
    const statusChip = (s: string) => <Chip size="small" variant="outlined" label={translate(`marketplaceOperator.orders.statuses.${s}`, { _: s })} />;
    const action = (o: OperatorOrder) => (
        o.refundable
            ? <Button size="small" variant="outlined" onClick={() => setRefunding(o)}>{translate('marketplaceOperator.orders.refund')}</Button>
            : <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>{translate('marketplaceOperator.orders.notRefundable')}</Typography>
    );

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.orders.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2 }}>{translate('marketplaceOperator.orders.intro')}</Typography>

            <Box
                component="form"
                onSubmit={(e: React.FormEvent) => { e.preventDefault(); setAppliedQuery(query.trim()); }}
                sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}
            >
                <TextField size="small" label={translate('marketplaceOperator.orders.search')} value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: { sm: 280 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="order-status-label">{translate('marketplaceOperator.orders.statusFilter')}</InputLabel>
                    <Select labelId="order-status-label" label={translate('marketplaceOperator.orders.statusFilter')} value={status} onChange={(e) => setStatus(String(e.target.value))}>
                        <MenuItem value="">{translate('marketplaceOperator.common.all')}</MenuItem>
                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{translate(`marketplaceOperator.orders.statuses.${s}`)}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="order-channel-label">{translate('marketplaceOperator.orders.channelFilter')}</InputLabel>
                    <Select labelId="order-channel-label" label={translate('marketplaceOperator.orders.channelFilter')} value={channel} onChange={(e) => setChannel(String(e.target.value))}>
                        <MenuItem value="">{translate('marketplaceOperator.common.all')}</MenuItem>
                        <MenuItem value="marketplace">{translate('marketplaceOperator.orders.channel.marketplace')}</MenuItem>
                        <MenuItem value="storefront">{translate('marketplaceOperator.orders.channel.storefront')}</MenuItem>
                    </Select>
                </FormControl>
                <Button type="submit" variant="outlined">{translate('marketplaceOperator.common.reload')}</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={200} />
            ) : orders.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.orders.empty')}</Alert>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['number', 'date', 'channel', 'parties', 'total', 'refunded', 'status', 'actions'].map((c) => (
                                        <TableCell key={c} align={c === 'total' || c === 'refunded' ? 'right' : 'left'}>{translate(`marketplaceOperator.orders.columns.${c}`)}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map((o) => (
                                    <TableRow key={o.uuid} data-testid={`order-${o.orderNumber}`}>
                                        <TableCell>{o.orderNumber}<Typography variant="caption" display="block" sx={{ color: 'var(--oa-text-2)' }}>{o.items.join(', ')}</Typography></TableCell>
                                        <TableCell>{fmt(o.createdAt)}</TableCell>
                                        <TableCell>{translate(`marketplaceOperator.orders.channel.${o.channel}`)}</TableCell>
                                        <TableCell>
                                            {party(o.sellerName, o.sellerCompanyId)}
                                            <Typography variant="caption" display="block" sx={{ color: 'var(--oa-text-2)' }}>
                                                {o.channel === 'storefront' ? o.customerEmail : party(o.buyerName, o.buyerCompanyId)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatEuro(o.total, o.currency)}</TableCell>
                                        <TableCell align="right">{Number(o.refundedAmount) > 0 ? formatEuro(o.refundedAmount, o.currency) : '–'}</TableCell>
                                        <TableCell>{statusChip(o.status)}</TableCell>
                                        <TableCell>{action(o)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                        {orders.map((o) => (
                            <Paper key={o.uuid} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontWeight: 600 }}>{o.orderNumber}</Typography>
                                    {statusChip(o.status)}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                                    {fmt(o.createdAt)} · {translate(`marketplaceOperator.orders.channel.${o.channel}`)} · {party(o.sellerName, o.sellerCompanyId)}
                                </Typography>
                                <Typography variant="body2">{formatEuro(o.total, o.currency)}{Number(o.refundedAmount) > 0 ? ` (−${formatEuro(o.refundedAmount, o.currency)})` : ''}</Typography>
                                <Box sx={{ mt: 1 }}>{action(o)}</Box>
                            </Paper>
                        ))}
                    </Box>
                </>
            )}

            <RefundDialog order={refunding} onClose={() => setRefunding(null)} onDone={() => { setRefunding(null); load(); }} />
        </Box>
    );
};

export default OperatorOrdersPanel;
